import {useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent, type PointerEvent, type ReactNode} from 'react';
import {TEAM_ROLES, getTeamRole, type TeamRole, type TeamRoleId} from '../src/team';
import {ErrorNotice} from './components';
import {RoleIcon} from './role-icon';
import type {NativeTeamProjection, TeamMemberView} from './team-workspace';
import './org-chart.css';

interface ChartNode {
  id: string;
  role: TeamRole;
  name: string;
  primary: boolean;
  member: TeamMemberView | null;
  children: ChartNode[];
}
interface NodePosition {
  node: ChartNode;
  x: number;
  y: number;
  width: number;
  height: number;
}
interface ChartLayout {
  nodes: NodePosition[];
  paths: string[];
  width: number;
  height: number;
}
interface NodeMeasurement {
  height: number;
  minimumWidth: number;
}
interface LayoutSize {
  label: string;
  width: number;
  gap: number;
  branch: number;
  height: number;
  childHeight: number;
  portrait: 32 | 48 | 64;
  childPortrait: 24 | 32 | 48;
  font: number;
  nameFont: number;
  childNameFont: number;
  padding: number;
}

const MINIMUM_TEXT_SIZE = 8;
const DETAIL_SIZE: LayoutSize = {
  label: 'detail', width: 176, gap: 24, branch: 36, height: 220, childHeight: 180,
  portrait: 48, childPortrait: 32, font: 12, nameFont: 16, childNameFont: 14, padding: 16,
};
const COMPACT_SIZE: LayoutSize = {
  label: 'compact', width: 176, gap: 24, branch: 24, height: 76, childHeight: 68,
  portrait: 32, childPortrait: 24, font: 12, nameFont: 12, childNameFont: 12, padding: 8,
};
const STRUCTURE_SIZE: LayoutSize = {
  label: 'structure', width: 176, gap: 32, branch: 72, height: 228, childHeight: 228,
  portrait: 64, childPortrait: 48, font: 16, nameFont: 21, childNameFont: 18, padding: 48,
};

function chartTree(projection: NativeTeamProjection | null): ChartNode {
  const members = new Map(projection?.members.map(member => [member.roleId, member]));
  const roles = new Map<TeamRoleId, ChartNode>(TEAM_ROLES.map(role => [role.id, {
    id: `role:${role.id}`, role, name: role.name, primary: true,
    member: members.get(role.id) ?? null, children: [],
  }]));
  const head = roles.get('head')!;
  head.children = TEAM_ROLES.filter(role => role.id !== 'head').map(role => roles.get(role.id)!);
  if (!projection) return head;
  const sessions = new Map<string, ChartNode>();
  for (const node of roles.values()) if (node.member) sessions.set(node.member.sessionId, node);
  for (const member of projection.descendants) sessions.set(member.sessionId, {
    id: `session:${member.sessionId}`, role: getTeamRole(member.roleId), name: member.name,
    primary: false, member, children: [],
  });
  for (const member of projection.descendants) {
    const parent = member.parentSessionId === null ? undefined : sessions.get(member.parentSessionId);
    if (!parent) throw new Error(`Cannot place agent ${member.sessionId}: parent ${member.parentSessionId} is missing.`);
    parent.children.push(sessions.get(member.sessionId)!);
  }
  return head;
}

function layoutChart(root: ChartNode, size: LayoutSize, measurements: Readonly<Record<string, NodeMeasurement>>): ChartLayout {
  interface Span {left: number; right: number; children: number; shift: number}
  const spans = new Map<string, Span>();
  const visiting = new Set<string>();
  const rowHeights: number[] = [];
  const nodeHeight = (node: ChartNode) => measurements[`${size.label}:${node.id}`]?.height ?? (node.primary ? size.height : size.childHeight);
  const nodeWidth = (node: ChartNode) => Math.max(size.width, measurements[`${size.label}:${node.id}`]?.minimumWidth ?? 0);
  function measure(node: ChartNode, depth: number): Span {
    if (visiting.has(node.id)) throw new Error(`Cannot lay out a repeated agent: ${node.id}.`);
    visiting.add(node.id);
    rowHeights[depth] = Math.max(rowHeights[depth] ?? 0, nodeHeight(node));
    const children = node.children.map(child => measure(child, depth + 1));
    const total = children.reduce((sum, child) => sum + child.left + child.right, 0)
      + Math.max(0, children.length - 1) * size.gap;
    const width = nodeWidth(node);
    const shift = node.primary && node.role.id === 'research' && children.length ? -width * .65 : 0;
    const span = {left: Math.max(width / 2, total / 2 - shift),
      right: Math.max(width / 2, total / 2 + shift), children: total, shift};
    spans.set(node.id, span);
    return span;
  }
  const full = measure(root, 0);
  const rowTops = [Math.min(20, size.padding)];
  for (let depth = 1; depth < rowHeights.length; depth++) {
    rowTops.push(rowTops[depth - 1]! + rowHeights[depth - 1]! + size.branch);
  }
  const padding = size.padding;
  const nodes: NodePosition[] = [];
  const paths: string[] = [];
  let bottom = 0;
  function place(node: ChartNode, center: number, depth: number): void {
    const y = rowTops[depth]!;
    const height = nodeHeight(node);
    const width = nodeWidth(node);
    nodes.push({node, x: center - width / 2, y, width, height});
    bottom = Math.max(bottom, y + height);
    if (!node.children.length) return;
    const span = spans.get(node.id)!;
    const nextY = rowTops[depth + 1]!;
    const busY = nextY - size.branch / 2;
    let left = center - span.children / 2 + span.shift;
    const drops: number[] = [];
    for (const child of node.children) {
      const childSpan = spans.get(child.id)!;
      const childCenter = left + childSpan.left;
      drops.push(childCenter);
      place(child, childCenter, depth + 1);
      left += childSpan.left + childSpan.right + size.gap;
    }
    const first = drops[0]!;
    const last = drops[drops.length - 1]!;
    paths.push(`M ${center} ${y + height} V ${busY}`);
    paths.push(`M ${Math.min(center, first)} ${busY} H ${Math.max(center, last)}`);
    for (const childCenter of drops) paths.push(`M ${childCenter} ${busY} V ${nextY}`);
  }
  place(root, padding + full.left, 0);
  return {nodes, paths, width: full.left + full.right + padding * 2, height: bottom + padding};
}

function nodeStatus(node: ChartNode, status: NativeTeamProjection['status']): string | null {
  if (status === 'loading') return null;
  if (status === 'unavailable') return 'Not connected';
  if (status === 'error') return 'Status unavailable';
  return node.member?.status ?? 'Not started';
}

type OrgChartProps = {mode: 'structure'} | {
  mode?: 'live';
  projection: NativeTeamProjection;
  selectedSessionId: string | null;
  onSelect: (sessionId: string | null) => void;
  renderUsage?: (member: TeamMemberView) => ReactNode;
};

export function OrgChart(props: OrgChartProps) {
  const structureOnly = props.mode === 'structure';
  const projection = structureOnly ? null : props.projection;
  const sessionId = projection?.sessionId;
  const chart = useRef<HTMLElement>(null);
  const viewport = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(0);
  const [viewportSize, setViewportSize] = useState({width: 0, height: 0});
  const [dragging, setDragging] = useState(false);
  const [nodeMeasurements, setNodeMeasurements] = useState<Record<string, NodeMeasurement>>({});
  const drag = useRef<{pointer: number; x: number; y: number; left: number; top: number} | null>(null);
  const pendingCenter = useRef<{x: number; y: number} | null>(null);
  const previousSession = useRef<string | null | undefined>(undefined);
  const previousSelection = useRef<string | undefined>(undefined);
  const tree = useMemo(() => chartTree(projection), [projection]);
  const layouts = useMemo(() => (structureOnly ? [STRUCTURE_SIZE] : [DETAIL_SIZE, COMPACT_SIZE])
    .map(size => layoutChart(tree, size, nodeMeasurements)), [tree, nodeMeasurements, structureOnly]);
  const measured = viewportSize.width > 0 && viewportSize.height > 0;
  const fittedScale = (candidate: ChartLayout) => measured
    ? Math.min(viewportSize.width / candidate.width, viewportSize.height / candidate.height, structureOnly ? Infinity : 1) : 1;
  const detailScale = fittedScale(layouts[0]!);
  const zoomFactor = 1.25 ** zoom;
  const minimumDetailScale = MINIMUM_TEXT_SIZE / DETAIL_SIZE.font;
  const detailZoomBase = structureOnly ? detailScale : Math.max(minimumDetailScale, detailScale);
  // The fitted live view stays compact when detail cannot fit readably. A deliberate
  // zoom starts from that readable floor, so its first step can show full detail.
  const compact = !structureOnly && zoom === 0 && detailScale < minimumDetailScale;
  const layout = layouts[compact ? 1 : 0]!;
  const size = structureOnly ? STRUCTURE_SIZE : compact ? COMPACT_SIZE : DETAIL_SIZE;
  const fitScale = fittedScale(layout);
  const scale = structureOnly ? fitScale * zoomFactor
    : Math.max(MINIMUM_TEXT_SIZE / size.font, fitScale) * zoomFactor;
  const displayedWidth = layout.width * scale;
  const displayedHeight = layout.height * scale;
  const stageWidth = Math.max(viewportSize.width, displayedWidth);
  const stageHeight = Math.max(viewportSize.height, displayedHeight);
  const canvasLeft = (stageWidth - displayedWidth) / 2;
  const canvasTop = (stageHeight - displayedHeight) / 2;
  const maximumZoom = Math.max(0, Math.ceil(Math.log(2 / detailZoomBase) / Math.log(1.25)));
  const canPan = measured
    && (displayedWidth > viewportSize.width + 1 || displayedHeight > viewportSize.height + 1);
  const selectedId = props.mode === 'structure' ? undefined
    : props.selectedSessionId === null ? 'role:head'
    : layout.nodes.find(item => item.node.member?.sessionId === props.selectedSessionId)?.node.id;
  const nodeIds = layout.nodes.map(item => item.node.id).join('|');
  const nodesMeasured = layout.nodes.every(item => nodeMeasurements[`${size.label}:${item.node.id}`] !== undefined);

  useLayoutEffect(() => {
    const box = viewport.current;
    const container = chart.current;
    if (!box || !container) return;
    let previousSize = {width: 0, height: 0};
    function measure(): void {
      const next = {width: box!.clientWidth, height: box!.offsetHeight};
      setViewportSize(current => current.width === next.width && current.height === next.height ? current : next);
      const containerSize = {width: container!.clientWidth, height: container!.clientHeight};
      if (containerSize.width !== previousSize.width || containerSize.height !== previousSize.height) {
        pendingCenter.current = null;
        setZoom(0);
        previousSize = containerSize;
      }
    }
    const observer = new ResizeObserver(measure);
    // Scrollbars from manual zoom must not trigger a return to the fitted view.
    observer.observe(box, {box: 'border-box'});
    observer.observe(container, {box: 'border-box'});
    measure();
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    const elements = viewport.current?.querySelectorAll<HTMLElement>('[data-org-node]');
    if (!elements) return;
    const nodes = Array.from(elements);
    const observedUsage = new Set<HTMLElement>();
    function record(): void {
      // Measure layout coordinates. The shared 3D renderer uses each visible slot's screen bounds.
      const measured = nodes.map(element => {
        const usage = element.querySelector<HTMLElement>('.org-chart-node-usage');
        const style = getComputedStyle(element);
        const minimumWidth = usage === null ? 0 : Math.ceil(usage.scrollWidth
          + Number.parseFloat(style.paddingLeft) + Number.parseFloat(style.paddingRight));
        return [`${size.label}:${element.dataset.orgNode}`, {height: element.offsetHeight, minimumWidth}] as const;
      });
      setNodeMeasurements(current => {
        const changed = measured.filter(([id, value]) => current[id]?.height !== value.height
          || current[id]?.minimumWidth !== value.minimumWidth);
        if (!changed.length) return current;
        return {...current, ...Object.fromEntries(changed)};
      });
    }
    const observer = new ResizeObserver(record);
    function observeUsage(): void {
      const usage = new Set(nodes.flatMap(element => Array.from(element.querySelectorAll<HTMLElement>('.org-chart-node-usage'))));
      for (const element of observedUsage) if (!usage.has(element)) {
        observer.unobserve(element);
        observedUsage.delete(element);
      }
      for (const element of usage) if (!observedUsage.has(element)) {
        observer.observe(element);
        observedUsage.add(element);
      }
      record();
    }
    // Token content can appear or grow while the node's own height stays unchanged.
    const mutations = new MutationObserver(observeUsage);
    for (const element of nodes) {
      observer.observe(element);
      mutations.observe(element, {childList: true, characterData: true, subtree: true});
    }
    observeUsage();
    return () => {observer.disconnect(); mutations.disconnect();};
  }, [nodeIds, size.label]);

  function centerNode(id: string, behavior: ScrollBehavior = 'smooth'): void {
    const box = viewport.current;
    const item = layout.nodes.find(item => item.node.id === id);
    if (!box || !item) return;
    box.scrollTo({left: canvasLeft + (item.x + item.width / 2) * scale - box.clientWidth / 2,
      top: canvasTop + item.y * scale - Math.max(24, (box.clientHeight - item.height * scale) / 2), behavior});
  }

  useLayoutEffect(() => {
    const box = viewport.current;
    if (!box) return;
    if (!structureOnly && previousSession.current !== sessionId) {
      previousSession.current = sessionId;
      pendingCenter.current = null;
      setZoom(0);
      box.scrollTo({left: 0, top: 0, behavior: 'instant'});
      return;
    }
    const center = pendingCenter.current;
    if (center) {
      box.scrollTo({left: canvasLeft + displayedWidth * center.x - box.clientWidth / 2,
        top: canvasTop + displayedHeight * center.y - box.clientHeight / 2, behavior: 'instant'});
      pendingCenter.current = null;
    } else if (zoom === 0) box.scrollTo({left: 0, top: 0, behavior: 'instant'});
  }, [sessionId, zoom, displayedWidth, displayedHeight, canvasLeft, canvasTop, structureOnly]);

  useLayoutEffect(() => {
    if (previousSelection.current === selectedId || !nodesMeasured) return;
    previousSelection.current = selectedId;
    if (selectedId) centerNode(selectedId);
  }, [selectedId, nodesMeasured]);

  function changeZoom(next: number): void {
    const target = Math.max(0, Math.min(maximumZoom, next));
    if (target === zoom) return;
    const box = viewport.current;
    if (box) pendingCenter.current = {x: (box.scrollLeft + box.clientWidth / 2 - canvasLeft) / displayedWidth,
      y: (box.scrollTop + box.clientHeight / 2 - canvasTop) / displayedHeight};
    setZoom(target);
  }

  function fit(): void {
    const box = viewport.current;
    if (!box) return;
    pendingCenter.current = null;
    setZoom(0);
    box.scrollTo({left: 0, top: 0, behavior: 'instant'});
  }

  function select(node: ChartNode): void {
    if (props.mode === 'structure') return;
    if (!node.member || props.projection.status !== 'ready') return;
    props.onSelect(node.primary && node.role.id === 'head' ? null : node.member.sessionId);
  }

  function startPan(event: PointerEvent<HTMLDivElement>): void {
    if (!canPan || event.button !== 0 || (event.target as HTMLElement).closest('button, a, input')) return;
    const box = event.currentTarget;
    drag.current = {pointer: event.pointerId, x: event.clientX, y: event.clientY, left: box.scrollLeft, top: box.scrollTop};
    box.setPointerCapture(event.pointerId);
    box.focus({preventScroll: true});
    setDragging(true);
    event.preventDefault();
  }
  function movePan(event: PointerEvent<HTMLDivElement>): void {
    const current = drag.current;
    if (!current || current.pointer !== event.pointerId) return;
    event.currentTarget.scrollLeft = current.left - (event.clientX - current.x);
    event.currentTarget.scrollTop = current.top - (event.clientY - current.y);
  }
  function stopPan(event: PointerEvent<HTMLDivElement>): void {
    if (drag.current?.pointer !== event.pointerId) return;
    drag.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    setDragging(false);
  }
  function keyboard(event: KeyboardEvent<HTMLDivElement>): void {
    if (event.target !== event.currentTarget) return;
    const distance = event.shiftKey ? 300 : 100;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.currentTarget.scrollBy({left: event.key === 'ArrowLeft' ? -distance : distance, behavior: 'smooth'});
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.currentTarget.scrollBy({top: event.key === 'ArrowUp' ? -distance : distance, behavior: 'smooth'});
    } else if (event.key === '+' || event.key === '=') changeZoom(zoom + 1);
    else if (event.key === '-') changeZoom(zoom - 1);
    else if (event.key === '0') fit();
    else if (event.key === 'Home') centerNode('role:head');
    else return;
    event.preventDefault();
  }

  return <section ref={chart} className={`org-chart${structureOnly ? ' org-chart-structure' : ' org-chart-live'}${compact ? ' org-chart-compact' : ''}`} aria-label="Team organization chart">
    {projection?.status === 'error' && <ErrorNotice title="Cannot read the team state" error={projection.error}/>}
    <div ref={viewport} className={`org-chart-viewport${canPan ? ' org-chart-can-pan' : ''}${dragging ? ' org-chart-dragging' : ''}`}
      tabIndex={0} role="region" aria-label="Agent reporting structure"
      aria-keyshortcuts="ArrowLeft ArrowRight ArrowUp ArrowDown = - 0 Home"
      onPointerDown={startPan} onPointerMove={movePan} onPointerUp={stopPan} onPointerCancel={stopPan}
      onLostPointerCapture={() => {drag.current = null; setDragging(false);}} onKeyDown={keyboard}>
      <div className="org-chart-stage" style={{width: stageWidth, height: stageHeight}}>
      <div className="org-chart-canvas" style={{width: layout.width, height: layout.height, '--org-font-size': `${size.font}px`,
        '--org-name-size': `${size.nameFont}px`, '--org-child-name-size': `${size.childNameFont}px`,
        position: 'absolute', left: canvasLeft, top: canvasTop, transform: `scale(${scale})`, transformOrigin: 'top left'} as CSSProperties}>
        <svg className="org-chart-connectors" width={layout.width} height={layout.height} aria-hidden="true">
          {layout.paths.map((path, index) => <path key={index} d={path}/>)}
        </svg>
        {layout.nodes.map(({node, x, y, width, height}) => {
          const status = projection ? nodeStatus(node, projection.status) : null;
          const selected = selectedId === node.id && node.member !== null;
          const activity = projection?.status === 'ready' ? node.member?.activity : null;
          const content = <>
            <RoleIcon roleId={node.role.id} size={node.primary ? size.portrait : size.childPortrait} decorative/>
            <span className="org-chart-node-name" title={node.name}>{node.name}</span>
            {props.mode !== 'structure' && node.member && props.renderUsage &&
              <span className="org-chart-node-usage">{props.renderUsage(node.member)}</span>}
            {!compact && structureOnly && <><span className="org-chart-node-title">{node.primary ? node.role.jobTitle : `${node.role.shortName} subagent`}</span>
              {node.primary && node.role.description && <span className="org-chart-node-description">{node.role.description}</span>}
            </>}
            {!compact && <>
              {activity && <span className="org-chart-node-activity" title={activity}><strong>Now:</strong> {activity}</span>}
              {status !== null && <span className="org-chart-node-status" data-state={node.member?.executionState ?? 'not-started'}>{status}</span>}</>}
          </>;
          return <div className={`org-chart-node${node.primary ? '' : ' org-chart-child'}`} key={node.id}
            style={{left: x, top: y, width, height}}>
            {structureOnly ? <div className="org-chart-node-content" data-org-node={node.id}>{content}</div>
            : <button type="button" className="org-chart-node-button" data-org-node={node.id} disabled={!node.member || projection?.status !== 'ready'}
              aria-label={`${node.name}. ${status}.${activity ? ` ${activity}` : ''}${node.member && projection?.status === 'ready' ? ' Open conversation.' : ''}`}
              aria-pressed={selected} onClick={() => select(node)}>
              {content}
            </button>}
          </div>;
        })}
      </div>
      </div>
    </div>
  </section>;
}
